import React from 'react';
import { Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import { useLegalDocuments, useLegalStatus, useMyLegalAgreements } from '../../hooks/useLegal';
import { legalApi, LegalAgreementRead } from '../../api/legal';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { calculateAge } from '../../utils/formatters';

interface FamilyMember {
  user_id: string;
  user_name: string;
  email?: string | null;
  dob?: string | null;
}

interface LegalDocumentsSectionProps {
  familyMemberships?: FamilyMember[];
  currentUserId?: string;
}

export default function LegalDocumentsSection({ familyMemberships, currentUserId }: LegalDocumentsSectionProps) {
  const { t, i18n } = useTranslation();
  const colors = useAppColors();
  const { data: docsData, isLoading: docsLoading } = useLegalDocuments(i18n.language);
  const { data: agreements, isLoading: agreementsLoading } = useMyLegalAgreements();
  const { data: legalStatus, isLoading: statusLoading } = useLegalStatus();

  if (docsLoading || agreementsLoading || statusLoading) {
    return (
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <LoadingIndicator />
      </View>
    );
  }

  if (!docsData || docsData.documents.length === 0) return null;

  // Build a set of doc_keys that are required for this user (filtered by audience by the backend)
  const requiredDocKeys = new Set((legalStatus?.required || []).map((r) => r.doc_key));

  // If no documents are required for this user, don't render the section
  if (requiredDocKeys.size === 0) return null;

  type AgreementItem = NonNullable<typeof agreements>[number];

  // Build a map of newest agreements by (doc_key, sub_user_id) combination
  const newestAgreements = new Map<string, AgreementItem>();
  // Also track guardian_waiver agreements by sub_user_id specifically
  const guardianWaiverByChild = new Map<string, AgreementItem>();

  if (agreements) {
    for (const a of agreements) {
      // For guardian_waiver, key by sub_user_id
      if (a.doc_key === 'guardian_waiver' && a.sub_user_id) {
        const existing = guardianWaiverByChild.get(a.sub_user_id);
        if (!existing || new Date(a.created_at) > new Date(existing.created_at)) {
          guardianWaiverByChild.set(a.sub_user_id, a);
        }
      } else {
        // For other docs, key by doc_key only
        const existing = newestAgreements.get(a.doc_key);
        if (!existing || new Date(a.created_at) > new Date(existing.created_at)) {
          newestAgreements.set(a.doc_key, a);
        }
      }
    }
  }

  // Get children (family members excluding current user and adult sub-users who sign their own docs)
  const children = (familyMemberships || []).filter(
    (m) => m.user_id !== currentUserId && (!m.dob || calculateAge(m.dob) < 18)
  );

  // Separate documents: guardian_waiver needs special handling
  // Only show documents that are required for this user's audience
  const regularDocs = docsData.documents.filter((d) => d.key !== 'guardian_waiver' && requiredDocKeys.has(d.key));
  const guardianWaiverDoc = requiredDocKeys.has('guardian_waiver')
    ? docsData.documents.find((d) => d.key === 'guardian_waiver')
    : undefined;

  const renderDocumentRow = (
    doc: typeof docsData.documents[0],
    agreement: AgreementItem | undefined,
    subtitle?: string
  ) => {
    const isCurrent = agreement ? agreement.version_hash === doc.version_hash : false;
    const statusText = agreement
      ? isCurrent
        ? t('legal.agreed')
        : t('legal.updateRequired')
      : t('legal.updateRequired');
    const statusColor = isCurrent ? colors.success || '#4caf50' : colors.warning || '#ff9800';

    return (
      <View key={subtitle ? `${doc.key}-${subtitle}` : doc.key} style={[styles.row, { borderBottomColor: colors.border }]}>
        <View style={styles.info}>
          <Text style={[styles.title, { color: colors.text }]}>
            {t(doc.title)}
            {subtitle && <Text style={[styles.childName, { color: colors.textSecondary }]}> · {subtitle}</Text>}
          </Text>
          <Text style={[styles.meta, { color: colors.textSecondary }]}>
            {doc.version} · {statusText}
          </Text>
          {agreement?.created_at && isCurrent && (
            <Text style={[styles.meta, { color: colors.textSecondary }]}>
              {new Date(agreement.created_at).toLocaleDateString()}
            </Text>
          )}
        </View>
        <View style={styles.actions}>
          <TouchableOpacity
            onPress={() => Linking.openURL(legalApi.getProxyDocUrl(doc.key, i18n.language, doc.version_hash))}
            style={[styles.actionBtn, { backgroundColor: colors.border }]}
          >
            <Icon name="file-document-outline" size={16} color={colors.text} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
        {t('legal.profileSection')}
      </Text>
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Regular documents (one per doc type) */}
        {regularDocs.map((doc) => {
          const agreement = newestAgreements.get(doc.key);
          return renderDocumentRow(doc, agreement);
        })}

        {/* Guardian waiver: one per child (only shown if required for this user) */}
        {guardianWaiverDoc && children.length > 0 && children.map((child) => {
          const agreement = guardianWaiverByChild.get(child.user_id);
          return renderDocumentRow(guardianWaiverDoc, agreement, child.user_name);
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { marginTop: 16, paddingHorizontal: 16 },
  sectionLabel: { fontSize: 12, fontWeight: '700', letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' },
  card: { borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  row: { flexDirection: 'row', alignItems: 'center', padding: 12, borderBottomWidth: 1 },
  info: { flex: 1 },
  title: { fontSize: 15, fontWeight: '600' },
  childName: { fontSize: 13, fontWeight: '400' },
  meta: { fontSize: 12, marginTop: 2 },
  actions: { flexDirection: 'row', gap: 8 },
  actionBtn: { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center' },
});
